import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useSolarStore } from '../../store/useSolarStore';
import { useNetworkStatus } from '../../lib/useNetworkStatus';
import {
  Sun,
  BatteryCharging,
  Zap,
  ShieldCheck,
  AlertTriangle,
  Wind,
  Refrigerator,
  Home,
  Cpu,
  Layers,
  Box,
  Maximize2,
  Activity,
  Flame,
  Radio,
  Tv,
  Droplets,
  Eye,
  Info,
  Sliders,
  Power,
  CheckCircle2,
  RotateCcw,
  Sparkles,
  TrendingUp,
  X
} from 'lucide-react';

type ViewMode = '3D_ISOMETRIC' | 'FRONT_RACK' | 'SCHEMATIC';
type SelectedNodeType = 'PV_ARRAY' | 'GRID' | 'INVERTER' | 'BATTERY' | 'LOAD' | null;

export const SolarSimulator: React.FC = () => {
  const {
    sunlightIntensity,
    setSunlight,
    gridAvailable,
    toggleGrid,
    currentSoc,
    getActivePreset,
    getTotalLoadWatts,
    getMetrics,
    timeOfDay,
    setTimeOfDay,
  } = useSolarStore();

  const { isLowBandwidth } = useNetworkStatus();

  const preset = getActivePreset();
  const totalLoad = getTotalLoadWatts();
  const metrics = getMetrics();

  // View & Interactive State
  const [viewMode, setViewMode] = useState<ViewMode>('3D_ISOMETRIC');
  const [selectedNode, setSelectedNode] = useState<SelectedNodeType>(null);
  const [is3DParallax, setIs3DParallax] = useState<boolean>(true);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  // Check if load exceeds inverter capacity
  const inverterCapacityWatts = preset.inverterCapacityKw * 1000;
  const isOverloaded = totalLoad > inverterCapacityWatts;
  const inverterLoadPct = Math.min(100, Math.round((totalLoad / inverterCapacityWatts) * 100));

  // Parallax Mouse Movement Handler
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!is3DParallax) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setMousePos({ x, y });
  };

  const handleMouseLeave = () => {
    setMousePos({ x: 0, y: 0 });
  };

  // Node telemetry inspector details generator
  const getNodeDetails = (node: SelectedNodeType) => {
    switch (node) {
      case 'PV_ARRAY':
        return {
          title: 'Monocrystalline Solar PV Array (3D Mounted)',
          model: 'Access Tech Tier-1 580W N-Type TOPCon Panels',
          status: sunlightIntensity > 10 ? 'Harvesting Sunlight' : 'Night / Standby',
          voltage: `${(48.5 + sunlightIntensity * 0.15).toFixed(1)} V DC`,
          current: `${((metrics.currentPowerGenerationKw * 1000) / 52).toFixed(1)} Amps`,
          power: `${metrics.currentPowerGenerationKw} kW`,
          efficiency: '22.8% Module Efficiency',
          temp: `${(32 + sunlightIntensity * 0.2).toFixed(1)} °C`,
          health: '100% Cell Nominal',
          description: '3D Angled glass-silicon modules converting direct photons into high-voltage DC power via dual MPPT trackers.',
        };
      case 'GRID':
        return {
          title: 'PHCN / Utility Grid Feed (3D Transformer Substation)',
          model: 'Band A 33kV Substation Feed (230V / 50Hz)',
          status: gridAvailable ? 'Online & Stable (Band A)' : 'Grid Outage / Disconnected',
          voltage: gridAvailable ? '230.4 V AC' : '0.0 V AC',
          frequency: gridAvailable ? '50.01 Hz' : '0.00 Hz',
          tariff: '₦225 / kWh (Band A Rate)',
          power: gridAvailable ? '1.2 kW Auxiliary Feed' : '0 kW (UPS Active)',
          temp: '38.2 °C',
          health: gridAvailable ? 'Grid Frequency Locked' : '0ms Seamless Transfer Active',
          description: 'National utility power feed. On blackout, the hybrid inverter seamlessly transfers household loads to battery storage in under 4ms.',
        };
      case 'INVERTER':
        return {
          title: 'Smart Hybrid Pure Sine Inverter Hub (3D Rackmount)',
          model: `Access Tech Industrial ${preset.capacityKva}kVA / ${preset.inverterCapacityKw}kW`,
          status: isOverloaded ? 'Warning: Inverter Overloaded' : 'Nominal Pure Sine Output',
          capacity: `${preset.capacityKva} kVA / ${preset.inverterCapacityKw} kW`,
          currentLoad: `${totalLoad} Watts (${inverterLoadPct}% Utilized)`,
          waveType: 'Pure Sine Wave (THD < 1%)',
          efficiency: '97.6% Hybrid Inversion',
          temp: `${(38 + (totalLoad / 1000) * 2.5).toFixed(1)} °C`,
          fanRpm: `${Math.round(1200 + (totalLoad / 1000) * 600)} RPM`,
          description: 'Central brain & power router managing MPPT solar optimization, battery charge regulation, grid synchronization, and pure sine AC output.',
        };
      case 'BATTERY':
        return {
          title: 'LiFePO4 Lithium Iron Phosphate Storage Wall (3D Blade Rack)',
          model: `Access Tech ${preset.batteryCapacityKwh}kWh High-Capacity Blade Wall`,
          status: currentSoc > 20 ? 'Battery Online & Discharging' : 'Low SOC Reserve',
          soc: `${currentSoc}% State of Charge`,
          capacity: `${preset.batteryCapacityKwh} kWh`,
          voltage: `${(51.2 * (currentSoc / 100) + 1.2).toFixed(1)} V DC`,
          backupHours: `${metrics.batteryRuntimeHours} Hours Remaining`,
          cycles: '6,000+ Cycles (15+ Year Lifespan)',
          temp: '29.4 °C (BMS Regulated)',
          description: 'Tier-1 Lithium Iron Phosphate cell chemistry with smart active cell balancing, thermal sensors, and cloud BMS telemetry.',
        };
      case 'LOAD':
        return {
          title: 'Connected Household & Facility Appliances (3D Smart Load)',
          model: 'Real-Time AC Distribution Panel',
          status: totalLoad > 0 ? 'Appliances Active' : 'Zero Demand',
          activePower: `${totalLoad} Watts (${(totalLoad / 1000).toFixed(2)} kW)`,
          dailyDemand: `${(totalLoad * 0.012 * 24).toFixed(1)} kWh / day`,
          breakdown: 'ACs, Deep Freezers, TVs, Water Pumps, Fans, LED Lighting',
          powerFactor: '0.98 PF (Clean Sine)',
          voltage: '229.8 V AC',
          description: 'Live power demand profile consumed concurrently by configured household and office electrical devices.',
        };
      default:
        return null;
    }
  };

  const selectedNodeInfo = getNodeDetails(selectedNode);

  return (
    <div className="bg-slate-900 border border-emerald-500/30 rounded-3xl p-6 sm:p-8 shadow-2xl text-white relative overflow-hidden my-8">
      {/* Background Ambient Glow */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-emerald-500/10 blur-[100px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 left-10 w-80 h-80 bg-cyan-500/10 blur-[90px] rounded-full pointer-events-none" />

      {/* Header & Controls Bar */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 pb-6 border-b border-slate-800">
        <div className="space-y-1">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-bold uppercase tracking-wider">
            <Sparkles className="w-3.5 h-3.5" /> Access Tech Smart Architecture & Flow
          </div>
          <h3 className="text-2xl sm:text-3xl font-black text-white tracking-tight flex items-center gap-3">
            Live 3D System Power Topology
            <span className="text-xs font-mono font-bold bg-slate-950 border border-emerald-500/40 text-emerald-400 px-3 py-1 rounded-full">
              INTERACTIVE 3D
            </span>
          </h3>
          <p className="text-xs sm:text-sm text-slate-300 max-w-2xl">
            Real-time energy flow visualization featuring interactive 3D hardware components, glowing busbars, directional electron pulses, and live telemetry.
          </p>
        </div>

        {/* View Mode & 3D Tilt Selector */}
        <div className="flex flex-wrap items-center gap-3 shrink-0">
          {/* Mode Switcher */}
          <div className="bg-slate-950 p-1.5 rounded-2xl border border-slate-800 flex items-center gap-1 text-xs font-medium">
            <button
              onClick={() => setViewMode('3D_ISOMETRIC')}
              className={`px-3 py-1.5 rounded-xl flex items-center gap-1.5 transition-all ${
                viewMode === '3D_ISOMETRIC'
                  ? 'bg-emerald-500 text-slate-950 font-extrabold shadow-md shadow-emerald-500/20'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Box className="w-4 h-4" /> 3D Stage
            </button>
            <button
              onClick={() => setViewMode('FRONT_RACK')}
              className={`px-3 py-1.5 rounded-xl flex items-center gap-1.5 transition-all ${
                viewMode === 'FRONT_RACK'
                  ? 'bg-emerald-500 text-slate-950 font-extrabold shadow-md shadow-emerald-500/20'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Layers className="w-4 h-4" /> Hardware Rack
            </button>
            <button
              onClick={() => setViewMode('SCHEMATIC')}
              className={`px-3 py-1.5 rounded-xl flex items-center gap-1.5 transition-all ${
                viewMode === 'SCHEMATIC'
                  ? 'bg-emerald-500 text-slate-950 font-extrabold shadow-md shadow-emerald-500/20'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Activity className="w-4 h-4" /> Diagram
            </button>
          </div>

          {/* 3D Tilt Toggle */}
          <button
            onClick={() => setIs3DParallax(!is3DParallax)}
            className={`p-2.5 rounded-2xl border transition-all text-xs flex items-center gap-2 ${
              is3DParallax
                ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-300'
                : 'bg-slate-950 border-slate-800 text-slate-400'
            }`}
            title="Toggle Mouse 3D Parallax Tilt"
          >
            <Eye className="w-4 h-4" />
            <span className="hidden sm:inline font-bold">3D Tilt</span>
          </button>

          {/* Inverter Status Badge */}
          <div
            className={`px-4 py-2 rounded-2xl border text-xs font-bold flex items-center gap-2 ${
              isOverloaded
                ? 'bg-rose-500/20 border-rose-500/50 text-rose-300 animate-pulse'
                : 'bg-emerald-500/20 border-emerald-500/40 text-emerald-300'
            }`}
          >
            {isOverloaded ? <AlertTriangle className="w-4 h-4 text-rose-400" /> : <ShieldCheck className="w-4 h-4 text-emerald-400" />}
            {isOverloaded ? 'Inverter Overloaded!' : `Inverter Nominal (${preset.capacityKva} kVA)`}
          </div>
        </div>
      </div>

      {/* Main Interactive 3D Canvas Stage */}
      <div
        className="relative py-8 w-full flex justify-center items-center min-h-[420px] sm:min-h-[460px] overflow-hidden rounded-3xl bg-slate-950/80 border border-slate-800/80 my-6 shadow-inner cursor-pointer"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {/* Interactive 3D Parallax Container */}
        <motion.div
          className="w-full max-w-5xl h-full flex items-center justify-center relative"
          animate={{
            rotateX: is3DParallax ? mousePos.y * -14 : 0,
            rotateY: is3DParallax ? mousePos.x * 18 : 0,
          }}
          transition={{ type: 'spring', stiffness: 120, damping: 20 }}
          style={{ transformStyle: 'preserve-3d', perspective: 1200 }}
        >
          {/* VIEW 1: 3D ISOMETRIC STAGE (Default Flagship Visual) */}
          {viewMode === '3D_ISOMETRIC' && (
            <svg className="w-full max-w-4xl h-[380px] sm:h-[420px]" viewBox="0 0 900 420" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                {/* 3D Floor Grid Pattern */}
                <pattern id="grid3d" width="40" height="40" patternUnits="userSpaceOnUse">
                  <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#1e293b" strokeWidth="0.8" opacity="0.6" />
                </pattern>

                {/* Metallic Gradients */}
                <linearGradient id="pvGlass" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#1e3a8a" />
                  <stop offset="50%" stopColor="#0f172a" />
                  <stop offset="100%" stopColor="#0284c7" />
                </linearGradient>

                <linearGradient id="inverterChassis" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#334155" />
                  <stop offset="50%" stopColor="#0f172a" />
                  <stop offset="100%" stopColor="#1e293b" />
                </linearGradient>

                <linearGradient id="batteryGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#0891b2" />
                  <stop offset="100%" stopColor="#0f172a" />
                </linearGradient>

                <filter id="glowGreen" x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur stdDeviation="6" result="blur" />
                  <feComposite in="SourceGraphic" in2="blur" operator="over" />
                </filter>
                <filter id="glowAmber" x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur stdDeviation="6" result="blur" />
                  <feComposite in="SourceGraphic" in2="blur" operator="over" />
                </filter>
                <filter id="glowCyan" x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur stdDeviation="6" result="blur" />
                  <feComposite in="SourceGraphic" in2="blur" operator="over" />
                </filter>
              </defs>

              {/* 3D Stage Floor Grid */}
              <rect width="900" height="420" fill="url(#grid3d)" rx="24" />

              {/* Glowing Floor Axis Lines */}
              <line x1="80" y1="210" x2="820" y2="210" stroke="#0f172a" strokeWidth="3" />

              {/* DYNAMIC PIPELINE 1: Solar Panel (150, 90) -> Inverter (450, 210) */}
              <path
                d="M 210 110 C 310 110, 360 210, 410 210"
                stroke={sunlightIntensity > 15 ? "#f59e0b" : "#334155"}
                strokeWidth="6"
                strokeLinecap="round"
                className="opacity-40"
              />
              <path
                d="M 210 110 C 310 110, 360 210, 410 210"
                stroke={sunlightIntensity > 15 ? "#fbbf24" : "#475569"}
                strokeWidth="3"
                strokeDasharray="8 8"
                className={sunlightIntensity > 15 && !isLowBandwidth ? "animate-flow-dash" : ""}
              />

              {/* DYNAMIC PIPELINE 2: Grid Transformer (150, 310) -> Inverter (450, 210) */}
              <path
                d="M 210 310 C 310 310, 360 210, 410 210"
                stroke={gridAvailable ? "#10b981" : "#f43f5e"}
                strokeWidth="6"
                strokeLinecap="round"
                className="opacity-40"
              />
              <path
                d="M 210 310 C 310 310, 360 210, 410 210"
                stroke={gridAvailable ? "#34d399" : "#f43f5e"}
                strokeWidth="3"
                strokeDasharray={gridAvailable ? "6 6" : "2 6"}
                className={gridAvailable && !isLowBandwidth ? "animate-flow-dash" : ""}
              />

              {/* DYNAMIC PIPELINE 3: Inverter (450, 210) <-> LiFePO4 Battery (710, 110) */}
              <path
                d="M 490 210 C 570 210, 620 110, 670 110"
                stroke="#06b6d4"
                strokeWidth="6"
                strokeLinecap="round"
                className="opacity-40"
              />
              <path
                d="M 490 210 C 570 210, 620 110, 670 110"
                stroke="#38bdf8"
                strokeWidth="3"
                strokeDasharray="8 8"
                className={!isLowBandwidth ? "animate-flow-dash" : ""}
              />

              {/* DYNAMIC PIPELINE 4: Inverter (450, 210) -> Household Loads (710, 310) */}
              <path
                d="M 490 210 C 570 210, 620 310, 670 310"
                stroke={isOverloaded ? "#f43f5e" : "#10b981"}
                strokeWidth="6"
                strokeLinecap="round"
                className="opacity-40"
              />
              <path
                d="M 490 210 C 570 210, 620 310, 670 310"
                stroke={isOverloaded ? "#f43f5e" : "#34d399"}
                strokeWidth="3"
                strokeDasharray="8 8"
                className={!isLowBandwidth ? "animate-flow-dash" : ""}
              />

              {/* ==================== 3D NODE 1: SOLAR PV ARRAY ==================== */}
              <g
                transform="translate(80, 50)"
                onClick={() => setSelectedNode('PV_ARRAY')}
                className="cursor-pointer group"
              >
                {/* 3D Angled Base Shadow */}
                <ellipse cx="70" cy="115" rx="65" ry="20" fill="#000000" opacity="0.6" filter="blur(8px)" />

                {/* 3D Frame Base Rack */}
                <path d="M 10 90 L 130 90 L 120 105 L 0 105 Z" fill="#334155" />

                {/* 3D Angled Silicon Panel Glass */}
                <polygon
                  points="10,25 130,10 145,85 10,100"
                  fill="url(#pvGlass)"
                  stroke={sunlightIntensity > 15 ? "#f59e0b" : "#475569"}
                  strokeWidth="2.5"
                  className="group-hover:stroke-amber-400 transition-all"
                />

                {/* Grid busbars on solar panel */}
                <line x1="40" y1="20" x2="40" y2="95" stroke="#38bdf8" strokeWidth="0.8" opacity="0.6" />
                <line x1="70" y1="17" x2="70" y2="92" stroke="#38bdf8" strokeWidth="0.8" opacity="0.6" />
                <line x1="100" y1="14" x2="100" y2="89" stroke="#38bdf8" strokeWidth="0.8" opacity="0.6" />
                <line x1="20" y1="55" x2="135" y2="42" stroke="#38bdf8" strokeWidth="0.8" opacity="0.6" />

                {/* Specular Light Reflection */}
                <polygon points="25,30 65,25 45,85 15,90" fill="#ffffff" opacity={sunlightIntensity > 30 ? "0.18" : "0.05"} />

                {/* Text Badge */}
                <rect x="15" y="102" width="115" height="24" rx="8" fill="#0f172a" stroke="#334155" />
                <text x="72" y="118" textAnchor="middle" fill="#f59e0b" fontSize="11" fontWeight="extrabold">
                  PV Array ({metrics.currentPowerGenerationKw}kW)
                </text>
              </g>

              {/* ==================== 3D NODE 2: PHCN SUBSTATION GRID ==================== */}
              <g
                transform="translate(80, 250)"
                onClick={() => setSelectedNode('GRID')}
                className="cursor-pointer group"
              >
                {/* 3D Shadow */}
                <ellipse cx="70" cy="110" rx="60" ry="18" fill="#000000" opacity="0.6" filter="blur(8px)" />

                {/* Transformer Metallic Block */}
                <rect x="20" y="20" width="100" height="70" rx="12" fill="#0f172a" stroke={gridAvailable ? "#10b981" : "#f43f5e"} strokeWidth="2.5" />
                <path d="M 20 20 L 40 5 L 140 5 L 120 20 Z" fill="#1e293b" stroke="#334155" />
                <path d="M 120 20 L 140 5 L 140 75 L 120 90 Z" fill="#020617" stroke="#334155" />

                {/* High Voltage Ceramic Insulator Bushings */}
                <rect x="40" y="-8" width="8" height="15" fill="#f59e0b" rx="2" />
                <rect x="70" y="-8" width="8" height="15" fill="#f59e0b" rx="2" />
                <rect x="100" y="-8" width="8" height="15" fill="#f59e0b" rx="2" />

                {/* Front Status Indicator */}
                <circle cx="40" cy="45" r="5" fill={gridAvailable ? "#10b981" : "#f43f5e"} className={gridAvailable ? "animate-pulse" : ""} />
                <text x="52" y="49" fill="#e2e8f0" fontSize="11" fontWeight="bold">
                  {gridAvailable ? "230V Band A" : "Blackout"}
                </text>
                <text x="52" y="66" fill="#94a3b8" fontSize="9">
                  {gridAvailable ? "50.01 Hz" : "Discos Cut"}
                </text>

                {/* Text Badge */}
                <rect x="15" y="98" width="115" height="24" rx="8" fill="#0f172a" stroke="#334155" />
                <text x="72" y="114" textAnchor="middle" fill={gridAvailable ? "#10b981" : "#f43f5e"} fontSize="11" fontWeight="extrabold">
                  PHCN Grid
                </text>
              </g>

              {/* ==================== 3D NODE 3: HYBRID INVERTER CENTERPIECE ==================== */}
              <g
                transform="translate(390, 140)"
                onClick={() => setSelectedNode('INVERTER')}
                className="cursor-pointer group"
              >
                {/* 3D Center Shadow & Aura Glow */}
                <ellipse cx="80" cy="135" rx="80" ry="22" fill="#000000" opacity="0.8" filter="blur(10px)" />
                <circle cx="80" cy="60" r="75" fill={isOverloaded ? "rgba(244, 63, 94, 0.15)" : "rgba(16, 185, 129, 0.12)"} filter="url(#glowGreen)" />

                {/* Inverter Main 3D Box Chassis */}
                <rect
                  x="10" y="10" width="140" height="110" rx="20"
                  fill="url(#inverterChassis)"
                  stroke={isOverloaded ? "#f43f5e" : "#10b981"}
                  strokeWidth="3.5"
                  className={isOverloaded ? "animate-pulse" : "animate-pulse-glow"}
                />
                {/* 3D Top Bevel Plane */}
                <path d="M 10 10 L 30 -5 L 170 -5 L 150 10 Z" fill="#1e293b" stroke="#334155" />
                <path d="M 150 10 L 170 -5 L 170 105 L 150 120 Z" fill="#020617" stroke="#334155" />

                {/* Digital LCD Telemetry Screen */}
                <rect x="28" y="25" width="104" height="42" rx="8" fill="#020617" stroke="#10b981" strokeWidth="1.5" />
                <text x="80" y="42" textAnchor="middle" fill="#10b981" fontSize="11" fontWeight="extrabold" fontFamily="monospace">
                  {preset.capacityKva} kVA INVERTER
                </text>
                <text x="80" y="58" textAnchor="middle" fill="#38bdf8" fontSize="10" fontFamily="monospace">
                  LOAD: {inverterLoadPct}% ({totalLoad}W)
                </text>

                {/* Dual Cooling Fan Vents */}
                <circle cx="48" cy="88" r="14" fill="#0f172a" stroke="#334155" />
                <circle cx="112" cy="88" r="14" fill="#0f172a" stroke="#334155" />
                <path d="M 48 76 L 48 100 M 36 88 L 60 88" stroke="#10b981" strokeWidth="2" className="animate-spin-slow" />
                <path d="M 112 76 L 112 100 M 100 88 L 124 88" stroke="#10b981" strokeWidth="2" className="animate-spin-slow" />

                {/* Center Badge */}
                <rect x="20" y="128" width="120" height="26" rx="10" fill="#0f172a" stroke="#10b981" strokeWidth="1.5" />
                <text x="80" y="145" textAnchor="middle" fill="#ffffff" fontSize="12" fontWeight="black">
                  HYBRID INVERTER
                </text>
              </g>

              {/* ==================== 3D NODE 4: LIFEPO4 BATTERY WALL ==================== */}
              <g
                transform="translate(650, 50)"
                onClick={() => setSelectedNode('BATTERY')}
                className="cursor-pointer group"
              >
                {/* 3D Shadow */}
                <ellipse cx="70" cy="115" rx="60" ry="18" fill="#000000" opacity="0.6" filter="blur(8px)" />

                {/* Battery Blade Cabinet Box */}
                <rect x="15" y="15" width="115" height="85" rx="16" fill="url(#batteryGrad)" stroke="#06b6d4" strokeWidth="2.5" />
                <path d="M 15 15 L 35 2 L 150 2 L 130 15 Z" fill="#1e293b" stroke="#334155" />
                <path d="M 130 15 L 150 2 L 150 87 L 130 100 Z" fill="#020617" stroke="#334155" />

                {/* Battery SOC 3D Bar Graph Cells */}
                <g transform="translate(30, 30)">
                  {[0, 1, 2, 3, 4].map((cellIdx) => {
                    const isCellFilled = currentSoc >= (cellIdx + 1) * 20;
                    return (
                      <rect
                        key={cellIdx}
                        x={cellIdx * 17}
                        y="0"
                        width="13"
                        height="30"
                        rx="3"
                        fill={isCellFilled ? "#06b6d4" : "#1e293b"}
                        stroke="#020617"
                      />
                    );
                  })}
                </g>

                <text x="72" y="76" textAnchor="middle" fill="#38bdf8" fontSize="11" fontWeight="bold" fontFamily="monospace">
                  SOC: {currentSoc}% ({preset.batteryCapacityKwh}kWh)
                </text>

                {/* Badge */}
                <rect x="15" y="102" width="115" height="24" rx="8" fill="#0f172a" stroke="#334155" />
                <text x="72" y="118" textAnchor="middle" fill="#06b6d4" fontSize="11" fontWeight="extrabold">
                  LiFePO4 Storage
                </text>
              </g>

              {/* ==================== 3D NODE 5: HOUSEHOLD ACTIVE LOAD ==================== */}
              <g
                transform="translate(650, 250)"
                onClick={() => setSelectedNode('LOAD')}
                className="cursor-pointer group"
              >
                {/* 3D Shadow */}
                <ellipse cx="70" cy="110" rx="60" ry="18" fill="#000000" opacity="0.6" filter="blur(8px)" />

                {/* 3D Isometric Building / Household Structure */}
                <polygon points="15,40 70,10 125,40 70,65" fill="#1e293b" stroke="#334155" strokeWidth="2" />
                <polygon points="15,40 70,65 70,95 15,70" fill="#0f172a" stroke="#334155" strokeWidth="2" />
                <polygon points="70,65 125,40 125,70 70,95" fill="#020617" stroke="#334155" strokeWidth="2" />

                {/* Illuminated Window Light */}
                <rect x="30" y="52" width="16" height="12" rx="2" fill="#fbbf24" opacity="0.9" filter="url(#glowAmber)" />
                <rect x="85" y="52" width="16" height="12" rx="2" fill="#38bdf8" opacity="0.9" filter="url(#glowCyan)" />

                {/* Badge */}
                <rect x="15" y="98" width="115" height="24" rx="8" fill="#0f172a" stroke="#334155" />
                <text x="72" y="114" textAnchor="middle" fill={isOverloaded ? "#f43f5e" : "#10b981"} fontSize="11" fontWeight="extrabold">
                  Active Load ({totalLoad}W)
                </text>
              </g>
            </svg>
          )}

          {/* VIEW 2: FRONT HARDWARE EQUIPMENT RACK */}
          {viewMode === 'FRONT_RACK' && (
            <div className="w-full max-w-2xl bg-slate-900 border-4 border-slate-800 rounded-3xl p-6 space-y-4 font-mono shadow-2xl">
              <div className="flex justify-between items-center pb-3 border-b border-slate-800 text-xs">
                <span className="text-emerald-400 font-bold flex items-center gap-2">
                  <Radio className="w-4 h-4 animate-pulse" /> ACCESS TECH 19" POWER RACK ENCLOSURE
                </span>
                <span className="text-slate-400">SERIAL: AT-RACK-8820</span>
              </div>

              {/* Shelf 1: Dual MPPT Solar Charger */}
              <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 flex justify-between items-center">
                <div>
                  <div className="text-amber-400 font-bold text-xs">UNIT 1: MPPT DUAL SOLAR CHARGE CONTROLLER</div>
                  <div className="text-[11px] text-slate-400 mt-1">
                    Solar V_in: {(48.2 + sunlightIntensity * 0.1).toFixed(1)}V • Power: {metrics.currentPowerGenerationKw} kW
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-amber-400 animate-pulse" />
                  <span className="w-3 h-3 rounded-full bg-emerald-400" />
                </div>
              </div>

              {/* Shelf 2: Hybrid Pure Sine Inverter Engine */}
              <div className="bg-slate-950 p-4 rounded-2xl border border-emerald-500/40 flex justify-between items-center shadow-lg shadow-emerald-500/10">
                <div>
                  <div className="text-emerald-400 font-bold text-xs">UNIT 2: {preset.capacityKva}kVA PURE SINE HYBRID INVERTER</div>
                  <div className="text-[11px] text-slate-400 mt-1">
                    Output: 230.4V @ 50.0Hz • Utilization: {inverterLoadPct}% ({totalLoad} Watts)
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Wind className="w-5 h-5 text-emerald-400 animate-spin" />
                  <span className="text-xs text-emerald-400 font-bold">ONLINE</span>
                </div>
              </div>

              {/* Shelf 3: LiFePO4 Blade Battery Rack */}
              <div className="bg-slate-950 p-4 rounded-2xl border border-cyan-500/40 flex justify-between items-center">
                <div>
                  <div className="text-cyan-400 font-bold text-xs">UNIT 3: LIFEPO4 BLADE BATTERY BANK</div>
                  <div className="text-[11px] text-slate-400 mt-1">
                    Capacity: {preset.batteryCapacityKwh} kWh • SOC: {currentSoc}% • Backup: {metrics.batteryRuntimeHours} Hours
                  </div>
                </div>
                <div className="w-24 bg-slate-800 h-3 rounded-full overflow-hidden">
                  <div className="bg-cyan-400 h-full rounded-full" style={{ width: `${currentSoc}%` }} />
                </div>
              </div>
            </div>
          )}

          {/* VIEW 3: SCHEMATIC FLOW DIAGRAM */}
          {viewMode === 'SCHEMATIC' && (
            <div className="w-full max-w-2xl bg-slate-950 p-6 rounded-3xl border border-slate-800 text-xs font-mono space-y-4">
              <div className="text-center text-slate-400 text-xs uppercase tracking-widest pb-2 border-b border-slate-800 font-sans font-bold">
                Singeline Electrical Schematic Diagram
              </div>

              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-slate-900 p-3 rounded-xl border border-amber-500/40">
                  <span className="text-amber-400 font-bold block">1. PV Input</span>
                  <span className="text-slate-300">{metrics.currentPowerGenerationKw} kW DC</span>
                </div>
                <div className="bg-slate-900 p-3 rounded-xl border border-emerald-500/40">
                  <span className="text-emerald-400 font-bold block">2. Inverter Bus</span>
                  <span className="text-slate-300">{preset.capacityKva} kVA</span>
                </div>
                <div className="bg-slate-900 p-3 rounded-xl border border-cyan-500/40">
                  <span className="text-cyan-400 font-bold block">3. Battery Storage</span>
                  <span className="text-slate-300">{currentSoc}% SOC</span>
                </div>
              </div>

              <div className="bg-slate-900 p-4 rounded-2xl border border-slate-800 flex justify-between items-center">
                <span>Distribution AC Busbar:</span>
                <strong className="text-emerald-400">230V AC @ 50.0Hz (Single Phase)</strong>
              </div>

              <div className="bg-slate-900 p-4 rounded-2xl border border-slate-800 flex justify-between items-center">
                <span>Connected Appliance Load:</span>
                <strong className="text-cyan-400">{totalLoad} Watts</strong>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Dynamic Telemetry Feedback Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4 border-t border-slate-800 text-xs">
        <div
          onClick={() => setSelectedNode('PV_ARRAY')}
          className="bg-slate-950 p-4 rounded-2xl border border-slate-800 hover:border-amber-500/50 flex items-center gap-3 transition-all cursor-pointer group"
        >
          <div className="w-10 h-10 rounded-xl bg-amber-500/20 text-amber-400 flex items-center justify-center shrink-0">
            <Sun className="w-5 h-5 animate-spin-slow" />
          </div>
          <div>
            <span className="text-slate-400 block text-[11px] group-hover:text-amber-300 transition-colors">PV Array Harvest</span>
            <span className="text-white font-bold text-sm">{metrics.currentPowerGenerationKw} kW</span>
          </div>
        </div>

        <div
          onClick={() => setSelectedNode('BATTERY')}
          className="bg-slate-950 p-4 rounded-2xl border border-slate-800 hover:border-cyan-500/50 flex items-center gap-3 transition-all cursor-pointer group"
        >
          <div className="w-10 h-10 rounded-xl bg-cyan-500/20 text-cyan-400 flex items-center justify-center shrink-0">
            <BatteryCharging className="w-5 h-5" />
          </div>
          <div>
            <span className="text-slate-400 block text-[11px] group-hover:text-cyan-300 transition-colors">LiFePO4 Storage</span>
            <span className="text-cyan-400 font-bold text-sm">{currentSoc}% ({preset.batteryCapacityKwh} kWh)</span>
          </div>
        </div>

        <div
          onClick={() => setSelectedNode('INVERTER')}
          className="bg-slate-950 p-4 rounded-2xl border border-slate-800 hover:border-emerald-500/50 flex items-center gap-3 transition-all cursor-pointer group"
        >
          <div className="w-10 h-10 rounded-xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0">
            <Wind className="w-5 h-5 animate-spin" />
          </div>
          <div>
            <span className="text-slate-400 block text-[11px] group-hover:text-emerald-300 transition-colors">Inverter Capacity</span>
            <span className="text-emerald-400 font-bold text-sm">{preset.capacityKva} kVA ({inverterLoadPct}%)</span>
          </div>
        </div>

        <div
          onClick={() => setSelectedNode('LOAD')}
          className="bg-slate-950 p-4 rounded-2xl border border-slate-800 hover:border-teal-500/50 flex items-center gap-3 transition-all cursor-pointer group"
        >
          <div className="w-10 h-10 rounded-xl bg-teal-500/20 text-teal-400 flex items-center justify-center shrink-0">
            <Zap className="w-5 h-5" />
          </div>
          <div>
            <span className="text-slate-400 block text-[11px] group-hover:text-teal-300 transition-colors">Active Household Load</span>
            <span className="text-teal-400 font-bold text-sm">{totalLoad} Watts</span>
          </div>
        </div>
      </div>

      {/* Node Inspector Drawer Modal */}
      <AnimatePresence>
        {selectedNode && selectedNodeInfo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4"
            onClick={() => setSelectedNode(null)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 max-w-lg w-full space-y-6 shadow-2xl relative text-white"
            >
              {/* Close Button */}
              <button
                onClick={() => setSelectedNode(null)}
                className="absolute top-5 right-5 p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="space-y-1">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-400 text-xs font-bold uppercase tracking-wider">
                  <Info className="w-3.5 h-3.5" /> 3D Hardware Telemetry Inspection
                </div>
                <h3 className="text-xl font-bold text-white pr-8">{selectedNodeInfo.title}</h3>
                <p className="text-xs text-emerald-400 font-mono">{selectedNodeInfo.model}</p>
              </div>

              <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-3 font-mono text-xs">
                <div className="flex justify-between border-b border-slate-900 pb-2">
                  <span className="text-slate-400">Operational Status:</span>
                  <strong className="text-emerald-400">{selectedNodeInfo.status}</strong>
                </div>
                {selectedNodeInfo.voltage && (
                  <div className="flex justify-between border-b border-slate-900 pb-2">
                    <span className="text-slate-400">Operating Voltage:</span>
                    <strong className="text-cyan-400">{selectedNodeInfo.voltage}</strong>
                  </div>
                )}
                {selectedNodeInfo.power && (
                  <div className="flex justify-between border-b border-slate-900 pb-2">
                    <span className="text-slate-400">Active Output:</span>
                    <strong className="text-amber-400">{selectedNodeInfo.power}</strong>
                  </div>
                )}
                {selectedNodeInfo.efficiency && (
                  <div className="flex justify-between border-b border-slate-900 pb-2">
                    <span className="text-slate-400">Conversion Rating:</span>
                    <strong className="text-emerald-400">{selectedNodeInfo.efficiency}</strong>
                  </div>
                )}
                {selectedNodeInfo.temp && (
                  <div className="flex justify-between">
                    <span className="text-slate-400">Thermal Sensor Temp:</span>
                    <strong className="text-white">{selectedNodeInfo.temp}</strong>
                  </div>
                )}
              </div>

              <p className="text-xs text-slate-300 leading-relaxed bg-slate-950/50 p-4 rounded-2xl border border-slate-800/80">
                {selectedNodeInfo.description}
              </p>

              <button
                onClick={() => setSelectedNode(null)}
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold py-3 rounded-2xl transition-colors text-xs uppercase tracking-wider"
              >
                Close Telemetry Inspection
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
